import { FILTER_FIELD_BY_KEY, fieldByToken } from './filter-fields';
import {
  FilterCondition,
  FilterField,
  FilterValue,
  QueryContext,
  QueryError,
  RangeBound,
} from './query.model';

/**
 * Splits a query at spaces outside quotes. Quotes stay in the items; a
 * backslash escapes a quote inside quotes.
 */
export const splitQuery = (text: string): string[] => {
  const items: string[] = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quoted && char === '\\' && text[i + 1] === '"') {
      current += '\\"';
      i++;
    } else if (char === '"') {
      quoted = !quoted;
      current += char;
    } else if (!quoted && /\s/.test(char)) {
      if (current) {
        items.push(current);
      }
      current = '';
    } else {
      current += char;
    }
  }
  if (current) {
    items.push(current);
  }
  return items;
};

/** Splits the values of a token at commas outside quotes and unquotes them. */
export const splitValues = (text: string): string[] | null => {
  const values: string[] = [];
  let current = '';
  let quoted = false;
  let wasQuoted = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quoted && char === '\\' && text[i + 1] === '"') {
      current += '"';
      i++;
    } else if (char === '"') {
      quoted = !quoted;
      wasQuoted = true;
    } else if (!quoted && char === ',') {
      values.push(current);
      current = '';
      wasQuoted = false;
    } else {
      current += char;
    }
  }
  if (quoted) {
    return null;
  }
  if (current || wasQuoted) {
    values.push(current);
  }
  return values.map((x) => x.trim()).filter((x) => x !== '');
};

const SUFFIXES: Record<string, number> = { k: 1e3, m: 1e6, bn: 1e9 };

/** A number with an optional suffix k, m or bn; null when it is none. */
export const parseNumber = (text: string): number | null => {
  const match = /^(-?\d+(?:\.\d+)?)(k|m|bn)?$/i.exec(text.trim());
  if (!match) {
    return null;
  }
  const factor = match[2] ? SUFFIXES[match[2].toLowerCase()] : 1;
  return Math.round(+match[1] * factor * 1e6) / 1e6;
};

const parseBound = (
  field: FilterField,
  text: string
): RangeBound | undefined => {
  if (text === '') {
    return null;
  }
  if (field.kind === 'year-range' || field.kind === 'date-range') {
    if (/^\d{4}$/.test(text)) {
      return +text;
    }
    const day = /^(\d{4})-(\d\d)-(\d\d)$/.exec(text);
    if (field.kind === 'date-range' && day) {
      const date = new Date(+day[1], +day[2] - 1, +day[3]);
      return date.getMonth() === +day[2] - 1 ? text : undefined;
    }
    return undefined;
  }
  return parseNumber(text) ?? undefined;
};

/** The next whole bound above or below a value, for `>` and `<`. */
const step = (bound: RangeBound, up: boolean): RangeBound => {
  if (typeof bound !== 'number') {
    return bound;
  }
  return Number.isInteger(bound) ? bound + (up ? 1 : -1) : bound;
};

const parseRange = (
  field: FilterField,
  text: string
): FilterValue | undefined => {
  const comparison = /^(>=|<=|>|<)(.*)$/.exec(text);
  if (comparison) {
    const bound = parseBound(field, comparison[2]);
    if (bound === undefined || bound === null) {
      return undefined;
    }
    const [operator] = comparison.slice(1);
    return operator.startsWith('>')
      ? {
          kind: 'range',
          from: operator === '>' ? step(bound, true) : bound,
          to: null,
        }
      : {
          kind: 'range',
          from: null,
          to: operator === '<' ? step(bound, false) : bound,
        };
  }
  const parts = text.split('..');
  if (parts.length > 2) {
    return undefined;
  }
  const from = parseBound(field, parts[0]);
  const to = parts.length === 2 ? parseBound(field, parts[1]) : from;
  if (
    from === undefined ||
    to === undefined ||
    (from === null && to === null)
  ) {
    return undefined;
  }
  return { kind: 'range', from, to };
};

const sameText = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/**
 * A subsector by its name, e.g. `HC - HCIT`; also by the part after the
 * sector (`HCIT`) or a part of the name when only one subsector has it.
 */
export const findByName = <T extends { name: string }>(
  items: T[],
  raw: string
): T | undefined => {
  const exact = items.find((x) => sameText(x.name, raw));
  if (exact) {
    return exact;
  }
  const lower = raw.toLowerCase();
  const suffix = items.filter((x) =>
    x.name.toLowerCase().endsWith(` ${lower}`)
  );
  if (suffix.length === 1) {
    return suffix[0];
  }
  const part = items.filter((x) => x.name.toLowerCase().includes(lower));
  return part.length === 1 ? part[0] : undefined;
};

/** The English name of a country code such as `DE`, as the API names countries. */
const regionName = (code: string) => {
  if (!/^[A-Za-z]{2}$/.test(code)) {
    return null;
  }
  try {
    return (
      new Intl.DisplayNames(['en'], { type: 'region' }).of(
        code.toUpperCase()
      ) ?? null
    );
  } catch {
    return null;
  }
};

const parseValues = (
  field: FilterField,
  text: string,
  context: QueryContext
): { value?: FilterValue; reason?: string } => {
  if (field.kind.endsWith('-range')) {
    const value = parseRange(field, text);
    return value ? { value } : { reason: 'filter.error.range' };
  }
  const values = splitValues(text);
  if (!values || !values.length) {
    return { reason: 'filter.error.empty' };
  }
  if (field.kind === 'text') {
    const value = values.join(', ');
    if (field.key === 'reviewer' && sameText(value, 'me')) {
      return context.userName
        ? { value: { kind: 'text', value: context.userName } }
        : { reason: 'filter.error.value' };
    }
    return { value: { kind: 'text', value } };
  }
  if (field.kind === 'enum') {
    const raw = values[values.length - 1];
    const alias = Object.entries(field.valueAliases ?? {}).find(([word]) =>
      sameText(word, raw)
    )?.[1];
    const option = alias ?? field.options?.find((x) => sameText(x, raw));
    return option
      ? { value: { kind: 'enum', values: [option] } }
      : { reason: 'filter.error.value' };
  }
  if (field.key === 'sectors') {
    const sectors = context.sectors ?? [];
    const ids: number[] = [];
    for (const raw of values) {
      const sector =
        findByName(sectors, raw) ??
        (/^\d+$/.test(raw) &&
        (!sectors.length || sectors.some((x) => x.id === +raw))
          ? { id: +raw }
          : undefined);
      if (!sector) {
        return { reason: 'filter.error.value' };
      }
      if (!ids.includes(sector.id)) {
        ids.push(sector.id);
      }
    }
    return { value: { kind: 'multi', values: ids } };
  }
  if (field.key === 'country_code') {
    const countries = context.countries ?? [];
    const names: string[] = [];
    for (const raw of values) {
      const name = countries.length
        ? (countries.find((x) => sameText(x, raw)) ??
          countries.find((x) => sameText(x, regionName(raw) ?? '')))
        : raw;
      if (!name) {
        return { reason: 'filter.error.value' };
      }
      if (!names.includes(name)) {
        names.push(name);
      }
    }
    return { value: { kind: 'multi', values: names } };
  }
  return { value: { kind: 'multi', values } };
};

export interface ParsedQuery {
  conditions: FilterCondition[];
  /** The words without a prefix, which became the name condition. */
  freeText: string;
  errors: QueryError[];
}

/** Removes the quotes of a free text word. */
const unquote = (item: string) =>
  /^".*"$/.test(item) ? item.slice(1, -1).replace(/\\"/g, '"') : item;

/**
 * Reads a query such as `sector:Payments country:DE,AT employees:50..500
 * acme`. Every field gets at most one condition: a later token of the same
 * field replaces the earlier one. Words without a prefix are the name.
 */
export const parseQuery = (
  text: string,
  context: QueryContext = {}
): ParsedQuery => {
  const conditions: FilterCondition[] = [];
  const errors: QueryError[] = [];
  const words: string[] = [];
  const put = (condition: FilterCondition) => {
    const index = conditions.findIndex((x) => x.field === condition.field);
    if (index >= 0) {
      conditions[index] = condition;
    } else {
      conditions.push(condition);
    }
  };
  for (const item of splitQuery(text)) {
    const token = /^([A-Za-z_]+):(.*)$/s.exec(item);
    if (!token) {
      if (!conditions.some((x) => x.field === 'name')) {
        conditions.push({ field: 'name', value: { kind: 'text', value: '' } });
      }
      words.push(unquote(item));
      continue;
    }
    const field = fieldByToken(token[1]);
    if (!field) {
      errors.push({ raw: item, reason: 'filter.error.field' });
      continue;
    }
    const { value, reason } = parseValues(field, token[2], context);
    if (value) {
      if (field.key === 'name') {
        words.length = 0;
      }
      put({ field: field.key, value });
    } else {
      errors.push({ raw: item, reason: reason ?? 'filter.error.value' });
    }
  }
  const freeText = words.join(' ');
  const nameCondition = conditions.find((x) => x.field === 'name');
  if (nameCondition && words.length) {
    nameCondition.value = { kind: 'text', value: freeText };
  }
  return {
    conditions: conditions.filter(
      (x) => !(x.value.kind === 'text' && x.value.value === '')
    ),
    freeText,
    errors,
  };
};

/** A value in quotes when it holds spaces, commas, quotes or a colon. */
export const quoteValue = (value: string) =>
  /[\s,":]|^$|^[<>]|\.\./.test(value)
    ? `"${value.replace(/"/g, '\\"')}"`
    : value;

/** A number with the suffix k, m or bn where it is exact. */
export const formatNumber = (value: number, money = false) => {
  if (money && value !== 0) {
    for (const [suffix, factor] of [
      ['bn', 1e9],
      ['m', 1e6],
      ['k', 1e3],
    ] as const) {
      const scaled = value / factor;
      if (
        Math.abs(scaled) >= 1 &&
        Math.round(scaled * 1000) === scaled * 1000
      ) {
        return `${scaled}${suffix}`;
      }
    }
  }
  return String(value);
};

const formatBound = (field: FilterField, bound: RangeBound) =>
  bound === null
    ? ''
    : typeof bound === 'number'
      ? formatNumber(bound, field.kind === 'money-range')
      : bound;

/** The values of a condition as the text after the prefix. */
export const formatValue = (
  condition: FilterCondition,
  context: QueryContext = {}
): string | null => {
  const field = FILTER_FIELD_BY_KEY.get(condition.field);
  const value = condition.value;
  if (!field || value.kind === 'raw') {
    return null;
  }
  if (value.kind === 'text') {
    return quoteValue(value.value);
  }
  if (value.kind === 'range') {
    if (value.from !== null && value.from === value.to) {
      return formatBound(field, value.from);
    }
    if (value.to === null) {
      return `>=${formatBound(field, value.from)}`;
    }
    if (value.from === null) {
      return `<=${formatBound(field, value.to)}`;
    }
    return `${formatBound(field, value.from)}..${formatBound(field, value.to)}`;
  }
  if (field.kind === 'enum') {
    const option = String(value.values[0]);
    const alias = Object.entries(field.valueAliases ?? {}).find(
      ([, x]) => x === option
    )?.[0];
    return alias ?? quoteValue(option);
  }
  const sectors = context.sectors ?? [];
  return value.values
    .map((x) => {
      if (field.key === 'sectors') {
        const named = sectors.filter((s) => s.id === x);
        const name = named.length === 1 ? named[0].name : null;
        // A name two subsectors share would not come back as the same id.
        return name &&
          sectors.filter((s) => sameText(s.name, name)).length === 1
          ? quoteValue(name)
          : String(x);
      }
      return quoteValue(String(x));
    })
    .join(',');
};

/**
 * The conditions as a query. The name is written as free text; conditions the
 * grammar cannot express (the companies of an alert, foreign operators) are
 * left out.
 */
export const formatQuery = (
  conditions: FilterCondition[],
  context: QueryContext = {}
): string =>
  conditions
    .map((condition) => {
      const field = FILTER_FIELD_BY_KEY.get(condition.field);
      if (!field || field.hidden) {
        return null;
      }
      const value = formatValue(condition, context);
      if (value === null) {
        return null;
      }
      return condition.field === 'name' ? value : `${field.token}:${value}`;
    })
    .filter((x): x is string => x !== null)
    .join(' ');
